import ApplicationSwitcherObservable, {
  SUBSCRIBE_ALL,
  UPDATE_MODEL,
  APP_DATA_LOADED,
} from './events/ApplicationSwitcherObservable';

import ApplicationSwitcherModel, {
  ApplicationLeaf,
  isApplicationBranch,
} from './model/ApplicationSwitcherModel';
import {
  DO_SEND_MESSAGE_TO_APP,
  VERSION,
  GOTO_APP,
  UPDATE_APP_NAME,
  PostMessageCommand,
  ORIGIN,
  SEND_MESSAGE_TO_APP,
  BROADCAST,
  SET_LOGOFF_URL,
  LOGOFF,
  TIMEOUT,
  UPDATE_DIRTY_STATE,
  SET_DIRTY_MESSAGE_ENABLED,
} from './messages/PostMessageCommand';
import ApplicationSwitcherConfig from './ApplicationSwitcherConfig';
import { eventBus, doSignoutEvent, EventData } from '../internal';

export const SUBSCRIBE_ALL_TYPES = SUBSCRIBE_ALL;
class ApplicationSwitcherClient {
  private observable: ApplicationSwitcherObservable = new ApplicationSwitcherObservable();
  private model: ApplicationSwitcherModel = {
    availableApplications: [],
    publicTypeSupportingApplications: new Map(),
  };
  private config: ApplicationSwitcherConfig;
  private messageQueue: { type: string; data: object }[] = [];
  private readyForMessages = false;
  private appId = '';

  constructor(config: ApplicationSwitcherConfig) {
    window.addEventListener('message', this.onMessage.bind(this));
    this.observable.subscribe(UPDATE_MODEL, this.updateModel);
    this.config = config;
    this.updateModel();
    eventBus.subscribe(doSignoutEvent, (_, eventData: EventData | undefined) => {
      if (eventData?.data.handled) {
        return;
      }

      if (eventData) {
        eventData.data.handled = true;
      }
      this.logoff();
    });

    // Initial app already has window.onpopstate set, so this will only be added to
    // subsequent apps.
    if (!window.onpopstate) {
      window.onpopstate = this.onPopState.bind(this);
    }
  }

  private onPopState() {
    if (!this.appId) return;
    this.gotoApp(this.appId, window.location.href, true);
  }

  private onMessage(event: MessageEvent) {
    let message;
    if (typeof event.data === 'string') {
      try {
        message = JSON.parse(event.data);
      } catch (e) {
        return;
      }
    } else {
      message = event.data;
    }

    if (message === null || message.version === undefined) return;

    switch (message.command) {
      case DO_SEND_MESSAGE_TO_APP:
        this.processMessage(message);
        break;
    }
  }

  private processMessage(message: { type: string; data: object }) {
    if (this.readyForMessages) {
      this.observable.fire(message.type, message.data);
    } else {
      this.messageQueue.push(message);
    }
  }

  async updateModel() {
    this.model.availableApplications = await this.config.applicationService.getAvailableApps();
    this.model.publicTypeSupportingApplications = await this.config.applicationService.getPublicTypeSupportingApps();
    this.observable.fire(APP_DATA_LOADED);
  }

  gotoApp(appId: string, url: string, isCompliant: boolean, options: object = {}) {
    const message = {
      version: VERSION,
      command: GOTO_APP,
      appId,
      url,
      isCompliant,
      options,
    };
    this.sendCommandToMain(message);
  }

  updateAppName(appId: string, appName: string) {
    const message = {
      version: VERSION,
      command: UPDATE_APP_NAME,
      appId,
      appName,
    };
    this.sendCommandToMain(message);
  }

  private sendCommandToMain(message: PostMessageCommand) {
    top?.window.postMessage(JSON.stringify(message), ORIGIN);
  }

  sendMessageToApp(appId: string, type: string, data?: any) {
    const message = {
      version: VERSION,
      command: SEND_MESSAGE_TO_APP,
      appId,
      type,
      data,
    };
    this.sendCommandToMain(message);
  }

  subscribe(type: string, fn: (type: string, data?: object) => void, context?: object) {
    this.observable.subscribe(type, fn, context);
  }

  unsubscribe(type: string, fn: (type: string, data?: object) => void, context?: object) {
    this.observable.unsubscribe(type, fn, context);
  }

  broadcastMessage(type: string, data: object = {}) {
    const message = {
      version: VERSION,
      command: BROADCAST,
      type,
      data,
    };
    this.sendCommandToMain(message);
  }

  setLogoffUrl(logoffUrl: string) {
    const message = {
      version: VERSION,
      command: SET_LOGOFF_URL,
      logoffUrl,
    };
    this.sendCommandToMain(message);
  }

  logoff() {
    const message = {
      version: VERSION,
      command: LOGOFF,
    };
    this.sendCommandToMain(message);
  }

  setDirtyMessageEnabled(allowDirtyMessage: boolean) {
    const setDirtyMessageEnabledMessage = {
      version: VERSION,
      command: SET_DIRTY_MESSAGE_ENABLED,
      allowDirtyMessage,
    };
    this.sendCommandToMain(setDirtyMessageEnabledMessage);
  }

  timeout(url: string) {
    const timeoutMessage = {
      version: VERSION,
      command: TIMEOUT,
      url,
    };
    this.sendCommandToMain(timeoutMessage);
  }

  updateDirtyFlag(appId: string, dirtyFlag: boolean) {
    const message = {
      version: VERSION,
      command: UPDATE_DIRTY_STATE,
      appId,
      dirtyFlag,
    };
    this.sendCommandToMain(message);
  }

  registerApp(appId: string, url: string, appName: string) {
    this.appId = appId;
    this.gotoApp(appId, url, true);
    this.updateAppName(appId, appName);
    this.readyForMessages = true;
    this.flushMessageQueue();
  }

  getModel() {
    return this.model;
  }

  openObject(objectType: string, objectInfo: any, openType: string, preferredAppId?: string) {
    const appId = this.getAppIdForType(objectType, preferredAppId);
    if (!appId) {
      return;
    }

    const app = this.getAvailableApp(appId);
    if (!app) {
      return;
    }

    this.gotoApp(app.appId, app.uri, app.appSwitcherCompliance);
    this.sendMessageToApp(app.appId, openType, {
      ...objectInfo,
      directive: app.directiveId,
      targetPublicType: objectType,
    });
  }

  private getAppIdForType(objectType: string, preferredAppId?: string): null | string {
    const type = this.model.publicTypeSupportingApplications.get(objectType);
    if (!type) {
      return null;
    }

    let appId = null;
    if (preferredAppId) {
      const filteredTypes = type.filter((appTypeInfo) => appTypeInfo.appId === preferredAppId);
      if (filteredTypes.length > 0) {
        appId = preferredAppId;
      }
    }

    if (!appId && type.length > 0) {
      appId = type[0].appId;
    }

    return appId;
  }

  public getAvailableApp(appId: string): ApplicationLeaf | null {
    let app = null;
    const appTree = this.model.availableApplications;
    for (const appNode of appTree) {
      if (isApplicationBranch(appNode)) {
        for (const appLeaf of appNode.children) {
          if (appLeaf.appId === appId) {
            app = appLeaf;
          }
        }
      } else if (appNode.appId === appId) {
        app = appNode;
      }
    }
    return app;
  }

  private flushMessageQueue(): void {
    for (const message of this.messageQueue) {
      this.observable.fire(message.type, message.data);
    }

    this.messageQueue = [];
  }
}

export default ApplicationSwitcherClient;
